/**
 * Communication Features Panel
 * Displays communication-related ML features including response quality, case manager interactions, and language
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import {
  MessageSquare,
  User,
  Users,
  Languages,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface Member {
  member_id: number;
  first_name: string;
  last_name: string;
  risk_level?: string;
}

interface CommunicationFeatures {
  // Response Quality
  f_response_sentiment_avg: number;
  f_positive_response_rate: number;
  f_response_action_accept_rate: number;
  f_response_delay_avg_hours: number;
  f_response_content_length_avg: number;
  f_response_engagement_score: number;
  f_responses_7d: number;
  f_responses_30d: number;

  // Case Manager
  f_case_manager_one_on_one_30d: number;
  f_case_manager_assigned_flag: boolean;
  f_case_manager_interaction_duration_avg: number;
  f_personalized_contact_rate: number;
  f_case_manager_one_on_one_success_rate: number;

  // Language
  f_language_preference_match: number;
  f_translation_used_flag: boolean;
  f_response_language_match: number;
  f_multilingual_engagement_score: number;
  f_language_barrier_detected: boolean;
}

interface CommunicationFeaturesPanelProps {
  memberId: number;
  cboId?: number;
}

export const CommunicationFeaturesPanel: React.FC<CommunicationFeaturesPanelProps> = ({
  memberId: propMemberId,
  cboId,
}) => {
  const { token: contextToken } = useAuth();
  const [features, setFeatures] = useState<CommunicationFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Member selection state (for standalone usage)
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<number>(propMemberId || 0);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Use prop memberId if provided, otherwise use selected
  const memberId = propMemberId && propMemberId > 0 ? propMemberId : selectedMemberId;

  // Helper function to get auth token
  const getAuthToken = (): string | null => {
    if (typeof window === 'undefined') return null;
    return contextToken || localStorage.getItem('auth_token');
  };

  // Fetch members for dropdown when no memberId is provided
  useEffect(() => {
    const authToken = getAuthToken();
    if ((!propMemberId || propMemberId <= 0) && authToken) {
      fetchMembers();
    }
  }, [propMemberId, contextToken]);

  const fetchMembers = async () => {
    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      setLoadingMembers(true);
      const activeCboId = cboId || localStorage.getItem('active_cbo_id') || '1';
      const response = await authFetch(`${getWeaveinApiUrl()}/api/retention/all-members/${activeCboId}?page=1&page_size=500`);
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || data || []);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    const authToken = getAuthToken();
    if (memberId && memberId > 0 && authToken) {
      fetchFeatures();
    } else if (!memberId || memberId <= 0) {
      setLoading(false);
      // Don't set error - we'll show member selector instead
    } else if (!authToken) {
      setLoading(false);
      setError('Authentication required');
    }
  }, [memberId, contextToken]);

  const fetchFeatures = async () => {
    if (!memberId || memberId <= 0) {
      setError('Invalid member ID');
      setLoading(false);
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`${getWeaveinApiUrl()}/api/ml/communication-features/${memberId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch communication features');
      }

      const data = await response.json();
      setFeatures(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load features');
      console.error('Error fetching communication features:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error && error !== 'Please select a member to view communication features') {
    return (
      <Card>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Show member selector when no member is selected
  if (!memberId || memberId <= 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Communication Features
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-muted-foreground" />
              <Label htmlFor="member-select" className="text-base font-medium">
                Select a Member
              </Label>
            </div>

            {loadingMembers ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading members...</span>
              </div>
            ) : members.length > 0 ? (
              <Select
                value={selectedMemberId > 0 ? selectedMemberId.toString() : ''}
                onValueChange={(value) => setSelectedMemberId(parseInt(value))}
              >
                <SelectTrigger id="member-select" className="w-full max-w-md">
                  <SelectValue placeholder="Choose a member to view communication features" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((member) => (
                    <SelectItem key={member.member_id} value={member.member_id.toString()}>
                      {member.first_name} {member.last_name}
                      {member.risk_level && (
                        <span className="ml-2 text-muted-foreground">({member.risk_level})</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-muted-foreground">No members found. Please ensure members are enrolled in your organization.</p>
            )}

            <div className="mt-6 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">About Communication Features</h4>
              <p className="text-sm text-muted-foreground">
                Communication features track member engagement patterns used for ML predictions:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                <li>Response quality and sentiment analysis</li>
                <li>Case manager interaction frequency and success rates</li>
                <li>Language preferences and translation needs</li>
                <li>Optimal contact timing and channel preferences</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!features) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Communication Features
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center h-64 text-center">
          <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No communication features available for this member</p>
          <p className="text-sm text-muted-foreground mt-2">
            Communication data will be collected as the member engages with your organization
          </p>
          <Button variant="outline" className="mt-4" onClick={() => setSelectedMemberId(0)}>
            Select Different Member
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Helper function to safely format numbers
  const safeToFixed = (value: number | undefined | null, decimals: number = 0): string => {
    if (value === undefined || value === null || isNaN(value)) return '0';
    return value.toFixed(decimals);
  };

  const safePercent = (value: number | undefined | null): string => {
    if (value === undefined || value === null || isNaN(value)) return '0';
    return (value * 100).toFixed(0);
  };

  const safeValue = (value: number | undefined | null): number => {
    if (value === undefined || value === null || isNaN(value)) return 0;
    return value;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Communication Features
          </CardTitle>
          <button onClick={fetchFeatures} className="text-muted-foreground hover:text-foreground">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="responses" className="w-full">
          <TabsList>
            <TabsTrigger value="responses">Response Quality</TabsTrigger>
            <TabsTrigger value="case-manager">Case Manager</TabsTrigger>
            <TabsTrigger value="language">Language</TabsTrigger>
          </TabsList>

          <TabsContent value="responses" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Response Sentiment</p>
                <div className="flex items-center gap-2">
                  <p
                    className={`text-2xl font-bold ${
                      safeValue(features.f_response_sentiment_avg) > 0.3
                        ? 'text-green-600'
                        : safeValue(features.f_response_sentiment_avg) < -0.3
                        ? 'text-red-600'
                        : 'text-yellow-600'
                    }`}
                  >
                    {safeValue(features.f_response_sentiment_avg) > 0.3
                      ? 'Positive'
                      : safeValue(features.f_response_sentiment_avg) < -0.3
                      ? 'Negative'
                      : 'Neutral'}
                  </p>
                  {safeValue(features.f_response_sentiment_avg) > 0.3 ? (
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  ) : safeValue(features.f_response_sentiment_avg) < -0.3 ? (
                    <TrendingDown className="h-5 w-5 text-red-600" />
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Score: {safeToFixed(features.f_response_sentiment_avg, 2)}
                </p>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Response Engagement</p>
                <p className="text-2xl font-bold">{safeToFixed(features.f_response_engagement_score)}</p>
                <Progress value={safeValue(features.f_response_engagement_score)} className="mt-2" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Positive Response Rate</p>
                <p className="text-xl font-bold">
                  {safePercent(features.f_positive_response_rate)}%
                </p>
              </div>

              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Accept Rate</p>
                <p className="text-xl font-bold">
                  {safePercent(features.f_response_action_accept_rate)}%
                </p>
              </div>

              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Avg Response Delay</p>
                <p className="text-xl font-bold flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {safeToFixed(features.f_response_delay_avg_hours, 1)}h
                </p>
              </div>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Response Activity</p>
              <div className="flex gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Last 7 Days</p>
                  <p className="text-lg font-bold">{features.f_responses_7d ?? 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Last 30 Days</p>
                  <p className="text-lg font-bold">{features.f_responses_30d ?? 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Avg Length</p>
                  <p className="text-lg font-bold">
                    {safeToFixed(features.f_response_content_length_avg)} chars
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="case-manager" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Case Manager Assigned</p>
                <div className="flex items-center gap-2">
                  {features.f_case_manager_assigned_flag ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <p className="text-lg font-bold">Yes</p>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-muted-foreground" />
                      <p className="text-lg font-bold">No</p>
                    </>
                  )}
                </div>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">One-on-One (30d)</p>
                <p className="text-2xl font-bold">{features.f_case_manager_one_on_one_30d ?? 0}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Avg Interaction Duration</p>
                <p className="text-xl font-bold">
                  {safeToFixed(features.f_case_manager_interaction_duration_avg)} min
                </p>
              </div>

              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Success Rate</p>
                <p className="text-xl font-bold">
                  {safePercent(features.f_case_manager_one_on_one_success_rate)}%
                </p>
              </div>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Personalized Contact Rate</p>
              <Progress value={safeValue(features.f_personalized_contact_rate) * 100} className="mb-2" />
              <p className="text-sm text-muted-foreground">
                {safePercent(features.f_personalized_contact_rate)}% of contacts are
                personalized vs bulk
              </p>
            </div>
          </TabsContent>

          <TabsContent value="language" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Language Preference Match</p>
                <p className="text-2xl font-bold">
                  {safePercent(features.f_language_preference_match)}%
                </p>
                <Progress value={safeValue(features.f_language_preference_match) * 100} className="mt-2" />
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Response Language Match</p>
                <p className="text-2xl font-bold">
                  {safePercent(features.f_response_language_match)}%
                </p>
                <Progress value={safeValue(features.f_response_language_match) * 100} className="mt-2" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Translation Used</p>
                <div className="flex items-center gap-2">
                  {features.f_translation_used_flag ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <p className="text-lg font-bold">Yes</p>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-muted-foreground" />
                      <p className="text-lg font-bold">No</p>
                    </>
                  )}
                </div>
              </div>

              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Language Barrier</p>
                <div className="flex items-center gap-2">
                  {features.f_language_barrier_detected ? (
                    <>
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      <p className="text-lg font-bold text-red-600">Detected</p>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <p className="text-lg font-bold">None</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Multilingual Engagement Score</p>
              <Progress value={safeValue(features.f_multilingual_engagement_score)} className="mb-2" />
              <p className="text-sm text-muted-foreground">
                {safeToFixed(features.f_multilingual_engagement_score)}/100 - Higher scores indicate
                better language-matched engagement
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default CommunicationFeaturesPanel;

